import React, { useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  ResponsiveContainer, RadarChart, PolarGrid,
  PolarAngleAxis as PolarAngleAxisBase,
  PolarRadiusAxis as PolarRadiusAxisBase,
  Radar, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from 'recharts';
import { HolisticStudent } from '../../../types';
import {
  FOUNDATIONAL_SCHEMA,
  ACADEMIC_DOMAINS,
  DOMAIN_COLORS,
  RUBRIC_LABELS,
  RUBRIC_COLORS,
  parseCompetencyMap,
  getDomainScore,
  getOverallScore,
  countRubricLevels,
  CompetencyMap,
} from '../data/foundationalSchema';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const PolarAngleAxis = PolarAngleAxisBase as any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const PolarRadiusAxis = PolarRadiusAxisBase as any;

// ─── Mini progress bar ───────────────────────────────────────────────────────
const MiniBar: React.FC<{ value: number; max: number; color: string }> = ({ value, max, color }) => (
  <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden flex-1">
    <div
      className="h-full rounded-full transition-all duration-700"
      style={{ width: `${max > 0 ? (value / max) * 100 : 0}%`, background: color }}
    />
  </div>
);

// ─── Rubric badge ────────────────────────────────────────────────────────────
const RubricBadge: React.FC<{ score: number | undefined }> = ({ score }) => {
  if (!score) return <span className="text-sm text-gray-850 italic font-medium">—</span>;
  const c = RUBRIC_COLORS[score];
  const darkerText = c.text.replace('-600', '-800').replace('-700', '-900');
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${c.bg} ${darkerText} border border-current/10`}>
      <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
      {RUBRIC_LABELS[score]}
    </span>
  );
};

// ─── Score ring ──────────────────────────────────────────────────────────────
const ScoreRing: React.FC<{ value: number; label: string; accent: string; size?: number }> = ({ value, label, accent, size = 68 }) => {
  const r = (size / 2) - 6;
  const circ = 2 * Math.PI * r;
  const offset = circ - (Math.min(100, value) / 100) * circ;
  return (
    <div className="flex flex-col items-center gap-1.5">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#E5E7EB" strokeWidth="5.5" />
        <circle
          cx={size / 2} cy={size / 2} r={r}
          fill="none" stroke={accent} strokeWidth="5.5"
          strokeLinecap="round"
          strokeDasharray={circ} strokeDashoffset={offset}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: 'stroke-dashoffset 1s ease' }}
        />
        <text x={size / 2} y={size / 2 + 4.5} textAnchor="middle" fontSize="12.5" fontWeight="800" fill={accent}>
          {value}%
        </text>
      </svg>
      <p className="text-xs text-gray-900 font-extrabold text-center leading-tight">{label}</p>
    </div>
  );
};

// ─── CG Accordion Card ───────────────────────────────────────────────────────
const CGCard: React.FC<{
  cg: typeof FOUNDATIONAL_SCHEMA[0];
  t1Map: CompetencyMap;
  t2Map: CompetencyMap;
  viewTerm: 'term1' | 'term2' | 'both';
  domainColor: { bg: string; text: string; border: string; accent: string };
}> = ({ cg, t1Map, t2Map, viewTerm, domainColor }) => {
  const [open, setOpen] = useState(false);

  const scores = cg.assessment.map(ind => ({
    ...ind,
    t1: t1Map[ind.entryKey],
    t2: t2Map[ind.entryKey],
  }));

  const filled = scores.filter(s => (viewTerm !== 'term2' ? s.t1 : s.t2) !== undefined);
  const proficient = filled.filter(s => (viewTerm !== 'term2' ? s.t1 : s.t2) === 3).length;
  const progressing = filled.filter(s => (viewTerm !== 'term2' ? s.t1 : s.t2) === 2).length;
  const beginner = filled.filter(s => (viewTerm !== 'term2' ? s.t1 : s.t2) === 1).length;
  const pct = filled.length ? Math.round(((proficient * 3 + progressing * 2 + beginner) / (filled.length * 3)) * 100) : 0;

  const darkerDomainText = domainColor.text.replace('-700', '-900');

  return (
    <div className={`rounded-xl border ${domainColor.border} overflow-hidden shadow-sm`}>
      <button
        className={`w-full flex items-center justify-between p-4.5 bg-gradient-to-r ${domainColor.bg} text-left`}
        onClick={() => setOpen(!open)}
      >
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-extrabold ${darkerDomainText} uppercase tracking-wider mb-1`}>
            CG-{cg.s_no}
          </p>
          <p className="text-base font-bold text-gray-900 leading-snug">{cg.curricularGoal}</p>
          <div className="flex items-center gap-4 mt-2.5">
            <div className="flex items-center gap-1.5 text-xs font-bold">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
              <span className="text-gray-900">{proficient} Proficient</span>
            </div>
            <div className="flex items-center gap-1.5 text-xs font-bold">
              <span className="w-2.5 h-2.5 rounded-full bg-amber-500" />
              <span className="text-gray-900">{progressing} Progressing</span>
            </div>
            <div className="flex items-center gap-1.5 text-xs font-bold">
              <span className="w-2.5 h-2.5 rounded-full bg-red-500" />
              <span className="text-gray-900">{beginner} Beginner</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3.5 flex-shrink-0 ml-4">
          <div className={`text-base font-black ${pct >= 80 ? 'text-emerald-700' : pct >= 60 ? 'text-amber-700' : 'text-red-700'}`}>
            {pct}%
          </div>
          <span className={`text-gray-900 font-bold transition-transform ${open ? 'rotate-180' : ''}`}>▼</span>
        </div>
      </button>

      {open && (
        <div className="bg-white">
          <div className="px-4 py-2 bg-gray-50 border-b border-gray-100 flex gap-4 text-xs font-bold text-gray-800 uppercase tracking-wider">
            <span className="flex-1">Indicator</span>
            {viewTerm !== 'term2' && <span className="w-28 text-center">Term 1</span>}
            {viewTerm !== 'term1' && <span className="w-28 text-center">Term 2</span>}
          </div>
          {scores.map((ind, i) => (
            <div
              key={ind.entryKey}
              className={`flex items-start gap-4 px-4 py-3.5 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'} border-b border-gray-100`}
            >
              <div className="flex-1 min-w-0">
                <span className="text-xs font-bold text-gray-900 mr-2">{ind.entryKey}</span>
                <span className="text-sm font-semibold text-gray-900">{ind.entryLabel}</span>
              </div>
              {viewTerm !== 'term2' && (
                <div className="w-28 flex justify-center"><RubricBadge score={ind.t1} /></div>
              )}
              {viewTerm !== 'term1' && (
                <div className="w-28 flex justify-center"><RubricBadge score={ind.t2} /></div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Academic Domain Card ────────────────────────────────────────────────────
const AcademicDomainCard: React.FC<{
  domain: typeof ACADEMIC_DOMAINS[0];
  t1Score: number;
  t2Score: number;
  t1Map: CompetencyMap;
  t2Map: CompetencyMap;
}> = ({ domain, t1Score, t2Score, t1Map, t2Map }) => {
  const [open, setOpen] = useState(false);
  const avgScore = t1Score > 0 && t2Score > 0 ? Math.round((t1Score + t2Score) / 2) : t1Score || t2Score;
  const cgs = FOUNDATIONAL_SCHEMA.filter(cg => domain.cgNumbers.includes(cg.s_no));

  const darkerDomainText = domain.text.replace('-700', '-900');

  return (
    <div className={`rounded-xl border ${domain.border} overflow-hidden shadow-sm`}>
      <div className={`p-4.5 bg-gradient-to-r ${domain.bg}`}>
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3.5 flex-1 min-w-0">
            <span className="text-3xl">{domain.icon}</span>
            <div className="flex-1 min-w-0">
              <p className={`text-base font-extrabold ${darkerDomainText} mb-0.5`}>{domain.label}</p>
              <p className="text-xs text-gray-800 font-semibold leading-snug mb-2.5">{domain.description}</p>
              {/* Progress bar */}
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-gray-900 w-16">Term 1</span>
                  <MiniBar value={t1Score} max={100} color={domain.accent} />
                  <span className="text-xs font-extrabold text-gray-900 w-10 text-right">{t1Score}%</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-gray-900 w-16">Term 2</span>
                  <MiniBar value={t2Score} max={100} color={domain.accent + 'cc'} />
                  <span className="text-xs font-extrabold text-gray-900 w-10 text-right">{t2Score}%</span>
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-col items-end gap-2.5">
            <div
              className={`text-2xl font-black ${avgScore >= 80 ? 'text-emerald-700' : avgScore >= 60 ? 'text-amber-700' : 'text-red-700'}`}
            >
              {avgScore}%
            </div>
            <div className={`text-xs font-bold px-2.5 py-0.5 rounded-full ${avgScore >= 80 ? 'bg-emerald-100 text-emerald-900' : avgScore >= 60 ? 'bg-amber-100 text-amber-900' : 'bg-red-100 text-red-900'
              }`}>
              {avgScore >= 80 ? 'Proficient' : avgScore >= 60 ? 'Progressing' : 'Beginner'}
            </div>
            <button
              onClick={() => setOpen(!open)}
              className="text-xs font-extrabold text-indigo-700 hover:text-indigo-900 underline transition"
            >
              {open ? 'Hide indicators' : 'View indicators'}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="bg-white">
          {cgs.map(cg => (
            <div key={cg.s_no} className="border-b border-gray-100 last:border-b-0">
              <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
                <p className="text-xs font-bold text-gray-900 uppercase tracking-wider">
                  CG-{cg.s_no}: {cg.curricularGoal}
                </p>
              </div>
              {cg.assessment.map((ind, i) => (
                <div
                  key={ind.entryKey}
                  className={`flex items-start gap-4 px-4 py-3 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/40'} border-b border-gray-50`}
                >
                  <span className="text-xs font-bold text-gray-900 mt-0.5 w-14 flex-shrink-0">{ind.entryKey}</span>
                  <p className="text-sm font-semibold text-gray-900 flex-1">{ind.entryLabel}</p>
                  <div className="flex gap-3 flex-shrink-0">
                    <div className="text-center">
                      <p className="text-xs font-bold text-gray-900 mb-0.5">T1</p>
                      <RubricBadge score={t1Map[ind.entryKey]} />
                    </div>
                    <div className="text-center">
                      <p className="text-xs font-bold text-gray-900 mb-0.5">T2</p>
                      <RubricBadge score={t2Map[ind.entryKey]} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Main CompetencyTab ───────────────────────────────────────────────────────
type TabMode = 'academic' | 'competency';

const CompetencyTab: React.FC = () => {
  const { student } = useOutletContext<{ student: HolisticStudent }>();
  const [mode, setMode] = useState<TabMode>('academic');
  const [viewTerm, setViewTerm] = useState<'term1' | 'term2' | 'both'>('both');
  const [domainFilter, setDomainFilter] = useState<string>('All');

  const t1Map = useMemo(() => parseCompetencyMap(student.academic?.term1_competency_raw), [student]);
  const t2Map = useMemo(() => parseCompetencyMap(student.academic?.term2_competency_raw), [student]);

  const t1Overall = useMemo(() => getOverallScore(t1Map), [t1Map]);
  const t2Overall = useMemo(() => getOverallScore(t2Map), [t2Map]);
  const t1Counts = useMemo(() => countRubricLevels(t1Map), [t1Map]);
  const t2Counts = useMemo(() => countRubricLevels(t2Map), [t2Map]);

  // AI scores from student record
  const aiScores = useMemo(() => ({
    confidence: student.aiScores?.confidence ?? 0,
    focus: student.aiScores?.focus ?? 0,
    motivation: student.aiScores?.motivation ?? 0,
    leadership: student.aiScores?.leadership ?? 0,
    peer: student.aiScores?.peer_interaction ?? 0,
    participation: student.aiScores?.participation ?? 0,
  }), [student]);

  // Academic domain scores
  const academicScores = useMemo(() => ACADEMIC_DOMAINS.map(domain => ({
    ...domain,
    t1Score: getDomainScore(domain.cgNumbers, t1Map),
    t2Score: getDomainScore(domain.cgNumbers, t2Map),
  })), [t1Map, t2Map]);

  // Radar chart data for academic view
  const radarData = useMemo(() => academicScores.map(d => ({
    subject: d.label.length > 15 ? d.label.slice(0, 15) + '…' : d.label,
    T1: d.t1Score,
    T2: d.t2Score,
  })), [academicScores]);

  // Domain filter for all-criteria view
  const domains = useMemo(() => ['All', ...Array.from(new Set(FOUNDATIONAL_SCHEMA.map(cg => cg.domain)))], []);
  const filteredCGs = useMemo(() => (
    domainFilter === 'All' ? FOUNDATIONAL_SCHEMA : FOUNDATIONAL_SCHEMA.filter(cg => cg.domain === domainFilter)
  ), [domainFilter]);

  const hasT1 = Object.keys(t1Map).length > 0;
  const hasT2 = Object.keys(t2Map).length > 0;

  // Check if we have any AI scores
  const hasAI = Object.values(aiScores).some(v => v > 0);

  return (
    <div className="space-y-5 min-w-0">
      {/* ── Mode Toggle ─────────────────────────────────────────────── */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex bg-gray-100 rounded-xl p-1 gap-1">
          <button
            onClick={() => setMode('academic')}
            className={`px-5 py-2.5 rounded-lg text-base font-bold transition-all ${mode === 'academic'
              ? 'bg-white text-indigo-900 shadow-sm'
              : 'text-gray-800 hover:text-gray-950'
              }`}
          >
            📊 Academic View
          </button>
          <button
            onClick={() => setMode('competency')}
            className={`px-5 py-2.5 rounded-lg text-base font-bold transition-all ${mode === 'competency'
              ? 'bg-white text-indigo-900 shadow-sm'
              : 'text-gray-800 hover:text-gray-950'
              }`}
          >
            📋 All Criteria (68 Indicators)
          </button>
        </div>

        {/* Term toggle */}
        <div className="flex bg-gray-100 rounded-xl p-1 gap-1 ml-auto">
          {(['both', 'term1', 'term2'] as const).map(t => (
            <button
              key={t}
              onClick={() => setViewTerm(t)}
              className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${viewTerm === t ? 'bg-white text-indigo-900 shadow-sm' : 'text-gray-800 hover:text-gray-950'
                }`}
            >
              {t === 'both' ? 'Both Terms' : t === 'term1' ? 'Term 1' : 'Term 2'}
            </button>
          ))}
        </div>
      </div>

      {/* ── Summary Header ──────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3.5 min-w-0">
        {hasT1 && (
          <div className="rounded-xl border border-indigo-150 bg-gradient-to-br from-indigo-50 to-violet-50 p-4.5 shadow-sm">
            <p className="text-xs font-bold text-indigo-900 uppercase tracking-wider mb-1">Term 1 Overall</p>
            <p className="text-3xl font-black text-indigo-900">{t1Overall}%</p>
            <div className="flex gap-2.5 mt-2.5 text-xs font-bold text-gray-900">
              <span className="text-emerald-700">✓ {t1Counts.proficient}</span>
              <span className="text-amber-700">◑ {t1Counts.progressing}</span>
              <span className="text-red-600">✗ {t1Counts.beginner}</span>
            </div>
          </div>
        )}
        {hasT2 && (
          <div className="rounded-xl border border-violet-150 bg-gradient-to-br from-violet-50 to-purple-50 p-4.5 shadow-sm">
            <p className="text-xs font-bold text-violet-900 uppercase tracking-wider mb-1">Term 2 Overall</p>
            <p className="text-3xl font-black text-violet-900">{t2Overall}%</p>
            <div className="flex gap-2.5 mt-2.5 text-xs font-bold text-gray-900">
              <span className="text-emerald-700">✓ {t2Counts.proficient}</span>
              <span className="text-amber-700">◑ {t2Counts.progressing}</span>
              <span className="text-red-600">✗ {t2Counts.beginner}</span>
            </div>
          </div>
        )}
        <div className="rounded-xl border border-emerald-150 bg-gradient-to-br from-emerald-50 to-teal-50 p-4.5 shadow-sm">
          <p className="text-xs font-bold text-emerald-900 uppercase tracking-wider mb-1">Behavior (AI)</p>
          <p className="text-3xl font-black text-emerald-900">{student.behaviorScore}</p>
          <p className="text-xs text-gray-800 font-bold mt-2">AI-analyzed from observations</p>
        </div>
        <div className="rounded-xl border border-sky-150 bg-gradient-to-br from-sky-50 to-cyan-50 p-4.5 shadow-sm">
          <p className="text-xs font-bold text-sky-900 uppercase tracking-wider mb-1">Peer Score</p>
          <p className="text-3xl font-black text-sky-900">{student.peerScore}</p>
          <p className="text-xs text-gray-800 font-bold mt-2">Social interaction rating</p>
        </div>
      </div>

      {/* ════════════════════════════════════════════════════════════════
          ACADEMIC VIEW
      ════════════════════════════════════════════════════════════════ */}
      {mode === 'academic' && (
        <div className="space-y-5">

          {/* AI Scores section */}
          {hasAI && (
            <div className="rounded-2xl border border-indigo-150 bg-white shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-indigo-50/60 to-violet-50/30">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-indigo-500 animate-pulse" />
                  <p className="text-base font-extrabold text-gray-900">AI-Computed Behavioral Scores</p>
                  <span className="ml-auto text-xs text-indigo-900 bg-indigo-50 px-2.5 py-1 rounded-full font-bold">
                    Sentence Transformer Model
                  </span>
                </div>
                <p className="text-sm text-gray-800 font-semibold mt-1 ml-4.5">
                  Derived from teacher &amp; parent descriptive feedback using NLP analysis
                </p>
              </div>
              <div className="p-5 grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3">
                {[
                  { label: 'Confidence', value: aiScores.confidence, icon: '💪', color: 'text-indigo-800', bg: 'from-indigo-50 to-violet-50', border: 'border-indigo-150' },
                  { label: 'Focus', value: aiScores.focus, icon: '🎯', color: 'text-blue-800', bg: 'from-blue-50 to-indigo-50', border: 'border-blue-150' },
                  { label: 'Motivation', value: aiScores.motivation, icon: '🚀', color: 'text-violet-800', bg: 'from-violet-50 to-purple-50', border: 'border-violet-150' },
                  { label: 'Leadership', value: aiScores.leadership, icon: '⭐', color: 'text-amber-800', bg: 'from-amber-50 to-orange-50', border: 'border-amber-150' },
                  { label: 'Peer Interaction', value: aiScores.peer, icon: '🤝', color: 'text-emerald-800', bg: 'from-emerald-50 to-teal-50', border: 'border-emerald-150' },
                  { label: 'Participation', value: aiScores.participation, icon: '🙋', color: 'text-sky-800', bg: 'from-sky-50 to-cyan-50', border: 'border-sky-150' },
                ].map(s => (
                  <div key={s.label} className={`rounded-xl border ${s.border} bg-gradient-to-br ${s.bg} p-3.5 shadow-sm min-w-0`}>
                    <div className="flex items-center justify-between mb-2.5">
                      <span className="text-xl">{s.icon}</span>
                      <span className={`text-xs font-bold px-2 py-0.5 rounded ${s.value >= 75 ? 'bg-emerald-100 text-emerald-900' : 'bg-amber-100 text-amber-900'
                        }`}>
                        {s.value >= 75 ? 'High' : 'Med'}
                      </span>
                    </div>
                    <p className={`text-2xl font-black ${s.color}`}>{s.value.toFixed(1)}</p>
                    <p className="text-xs font-bold text-gray-800">{s.label}</p>
                    <div className="mt-2.5 h-1.5 bg-gray-100 rounded-full">
                      <div className="h-full rounded-full" style={{ width: `${s.value}%`, backgroundColor: s.color.replace('text-', '#').replace('-800', '') + '80' || '#6366f1' }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Radar chart */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-5 min-w-0">
            <div className="lg:col-span-3 rounded-2xl border border-gray-100 bg-white shadow-sm p-5 min-w-0">
              <p className="text-base font-extrabold text-gray-900 mb-1">Academic Domain Radar</p>
              <p className="text-xs text-gray-800 font-bold mb-4">Based on teacher rubric assessments (1=Beginner, 2=Progressing, 3=Proficient)</p>
              <ResponsiveContainer width="100%" height={280}>
                <RadarChart data={radarData}>
                  <PolarGrid stroke="#E5E7EB" />
                  <PolarAngleAxis dataKey="subject" tick={{ fontSize: 9, fill: '#6B7280' }} />
                  <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 9, fill: '#9CA3AF' }} />
                  <Radar name="Term 1" dataKey="T1" stroke="#6366F1" fill="#6366F1" fillOpacity={0.15} strokeWidth={2} dot={{ r: 3, fill: '#6366f1' }} />
                  <Radar name="Term 2" dataKey="T2" stroke="#7C3AED" fill="#7C3AED" fillOpacity={0.1} strokeWidth={2} strokeDasharray="4 2" dot={{ r: 3, fill: '#7c3aed' }} />
                  <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12, border: '1px solid #E0E7FF' }} formatter={(v: any) => [`${v}%`]} />
                </RadarChart>
              </ResponsiveContainer>
              <div className="flex justify-center gap-5 mt-2">
                <div className="flex items-center gap-1.5 text-xs text-gray-500">
                  <span className="w-5 h-0.5 bg-indigo-500 inline-block rounded" />Term 1
                </div>
                <div className="flex items-center gap-1.5 text-xs text-gray-500">
                  <span className="w-5 h-0.5 border-b-2 border-dashed border-violet-500 inline-block" />Term 2
                </div>
              </div>
            </div>

            {/* Score rings by domain */}
            <div className="lg:col-span-2 rounded-2xl border border-gray-100 bg-white shadow-sm p-5 min-w-0">
              <p className="text-base font-extrabold text-gray-900 mb-4">Domain Scores</p>
              <div className="grid grid-cols-4 gap-3">
                {academicScores.map(d => (
                  <ScoreRing
                    key={d.id}
                    value={d.t1Score > 0 && d.t2Score > 0 ? Math.round((d.t1Score + d.t2Score) / 2) : d.t1Score || d.t2Score}
                    label={d.label.split(' ').slice(0, 2).join(' ')}
                    accent={d.accent}
                    size={68}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Academic Domain Cards */}
          <div className="space-y-3.5">
            <p className="text-base font-extrabold text-gray-900">Academic Competency by Domain</p>
            {academicScores.map(domain => (
              <AcademicDomainCard
                key={domain.id}
                domain={domain}
                t1Score={domain.t1Score}
                t2Score={domain.t2Score}
                t1Map={t1Map}
                t2Map={t2Map}
              />
            ))}
          </div>

          {/* Bar chart: domain comparison */}
          <div className="rounded-2xl border border-gray-100 bg-white shadow-sm p-5 min-w-0">
            <p className="text-base font-extrabold text-gray-900 mb-1">Term 1 vs Term 2 — Domain Comparison</p>
            <p className="text-xs text-gray-800 font-bold mb-4">Scores normalized to 0-100% based on rubric (Proficient = 100%)</p>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={academicScores.map(d => ({ name: d.label.split(' ').slice(0, 2).join('\n'), T1: d.t1Score, T2: d.t2Score }))} barGap={4} barCategoryGap="25%">
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F3F4F6" />
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} formatter={(v: any) => [`${v}%`]} />
                <Bar dataKey="T1" name="Term 1" radius={[4, 4, 0, 0]} fill="#6366F1" />
                <Bar dataKey="T2" name="Term 2" radius={[4, 4, 0, 0]} fill="#7C3AED" fillOpacity={0.7} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════
          ALL CRITERIA VIEW (68 Indicators)
      ════════════════════════════════════════════════════════════════ */}
      {mode === 'competency' && (
        <div className="space-y-4">
          {/* Domain filter pills */}
          <div className="flex flex-wrap gap-2">
            {domains.map(d => (
              <button
                key={d}
                onClick={() => setDomainFilter(d)}
                className={`text-sm font-bold px-4 py-2 rounded-full border transition-all ${domainFilter === d
                  ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                  : 'bg-white text-gray-850 border-gray-200 hover:border-indigo-300'
                  }`}
              >
                {d === 'All' ? '🗂 All Domains' : d}
              </button>
            ))}
          </div>

          {/* CG Cards */}
          <div className="space-y-3">
            {filteredCGs.map(cg => {
              const domColor = DOMAIN_COLORS[cg.domain] || {
                bg: 'from-gray-50 to-gray-100', text: 'text-gray-700',
                border: 'border-gray-200', accent: '#6b7280',
              };
              return (
                <CGCard
                  key={cg.s_no}
                  cg={cg}
                  t1Map={t1Map}
                  t2Map={t2Map}
                  viewTerm={viewTerm}
                  domainColor={domColor}
                />
              );
            })}
          </div>

          {!hasT1 && !hasT2 && (
            <div className="text-center py-12 text-gray-400">
              <p className="text-4xl mb-3">📋</p>
              <p className="text-sm font-medium">No competency data available for this student</p>
              <p className="text-xs mt-1">Teacher rubric scores will appear here once submitted</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CompetencyTab;
